import { useTranslation } from "react-i18next";

import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { SaveStatus } from "@/components/SaveStatus";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { saveProfile, useProfileQuery } from "../api";
import type { CommunicationProfile, PatchedCommunicationProfile } from "../types";
import { TextAreaField } from "./fields";

// The communication profile step holds only the narrative (mission / vision /
// values / SD commitments) plus the showcase toggle — parity with the legacy
// authoring flow. Enterprise identity and contact details come from the
// identification profile and are intentionally not re-editable here.
function toValues(profile: CommunicationProfile): PatchedCommunicationProfile {
  return {
    mission: profile.mission ?? "",
    vision: profile.vision ?? "",
    values: profile.values ?? "",
    sustainable_development_commitments:
      profile.sustainable_development_commitments ?? "",
    show_on_pme02_display: profile.show_on_pme02_display ?? false,
  };
}

function ProfileForm({ profile }: { profile: CommunicationProfile }) {
  const { t } = useTranslation();

  const { form, autosave } = useAutosaveForm<PatchedCommunicationProfile>({
    defaultValues: toValues(profile),
    autosave: {
      save: (values, signal) => saveProfile(values, signal),
      debounceMs: 600,
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <div className="space-y-1.5">
          <CardTitle>{t("communications.profile.title")}</CardTitle>
          <CardDescription>
            {t("communications.profile.description")}
          </CardDescription>
        </div>
        <SaveStatus status={autosave.status} />
      </CardHeader>
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <form.Field name="mission">
              {(field) => (
                <TextAreaField
                  field={field}
                  label={t("communications.profile.mission")}
                  maxLength={800}
                  description={t("communications.profile.charLimit")}
                />
              )}
            </form.Field>
            <form.Field name="vision">
              {(field) => (
                <TextAreaField
                  field={field}
                  label={t("communications.profile.vision")}
                  maxLength={800}
                  description={t("communications.profile.charLimit")}
                />
              )}
            </form.Field>
          </div>

          <form.Field name="values">
            {(field) => (
              <TextAreaField
                field={field}
                label={t("communications.profile.values")}
                maxLength={800}
                description={t("communications.profile.charLimit")}
              />
            )}
          </form.Field>

          <form.Field name="sustainable_development_commitments">
            {(field) => (
              <TextAreaField
                field={field}
                label={t("communications.profile.commitments")}
                maxLength={800}
                description={t("communications.profile.charLimit")}
              />
            )}
          </form.Field>

          <form.Field name="show_on_pme02_display">
            {(field) => (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    id={field.name}
                    name={field.name}
                    type="checkbox"
                    className="size-4 rounded border-input"
                    checked={Boolean(field.state.value)}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    onBlur={field.handleBlur}
                  />
                  <Label htmlFor={field.name}>
                    {t("communications.profile.showOnDisplay")}
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("communications.profile.displayHint")}{" "}
                  <a
                    href="https://pmedurable02.com/lactivateur/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    PME Durable 02
                  </a>
                </p>
              </div>
            )}
          </form.Field>
        </form>
      </CardContent>
    </Card>
  );
}

export function ProfileStep() {
  const { t } = useTranslation();
  const query = useProfileQuery();

  if (query.isPending) {
    return <p className="text-muted-foreground">{t("communications.loading")}</p>;
  }
  if (query.isError || !query.data) {
    return (
      <p className="text-destructive">{t("communications.profile.loadError")}</p>
    );
  }

  // Key on the profile id so the form's defaultValues initialise once from the
  // loaded server state (and re-init if the profile identity ever changes).
  return <ProfileForm key={query.data.id} profile={query.data} />;
}
